import React, { PureComponent } from 'react';
import { MapContext } from 'react-openlayers';
import { getFeatureType, isScheduleOverlay } from '../helpers/features';
import {
  filterDraggable,
  DELETEABLE_FEATURES_BY_JOB,
  ZONE_TYPE_CHANGEABLE_FEATURES_BY_JOB,
  WAYPOINT_SETTABLE_FEATURES_BY_JOB,
  SKIP_SETTABLE_FEATURES_BY_JOB,
  POINT_MOVABLE_FEATURES_BY_JOB,
  SELECTION_PRIORITIES,
} from '../helpers/selectionRules';
import { KML_FEATURE_TYPE, KmlFeatureType } from '../../../featureTypes';
import { doubleClick } from 'ol/events/condition';
import EventBus from '../../../EventBus';
import { MISSION_EVENTS } from '../../../missionEvents';
import { findMaxLat } from '../../../utils';
import { MAP_TYPES, MapType } from '../helpers/mapTypes';
import Feature, { FeatureLike } from 'ol/Feature';
import { SimpleGeometry } from 'ol/geom';
import { SelectCandidate, JobType } from '../../../types/types';
import { Coordinate } from 'ol/coordinate';
import Select, { SelectEvent } from 'ol/interaction/Select';
import Modify, { ModifyEvent } from 'ol/interaction/Modify';
import BaseEvent from 'ol/events/Event';
import { MAP_EVENTS } from '../../../mapEvents';
import { queryParams } from '../../../services/QueryParams';
import selectionManager from '../../../services/SelectionManager';

type AddSelectionCandidate = (candidate: SelectCandidate | null) => void;

interface MapInteractionsLayerProps {
  addDeleteCandidate: AddSelectionCandidate;
  addZoneTypeChangeCandidate: AddSelectionCandidate;
  addSkipCandidate: AddSelectionCandidate;
  addWaypointCandidate: AddSelectionCandidate;
  addMoveCandidate: AddSelectionCandidate;
  showZones: boolean;
  zoneMission: boolean;
  jobType: JobType;
  updatePullin: (pullin: Coordinate) => void;
  updateParkingPoint: (coord: Coordinate) => void;
  updateStartingPoint: (coord: Coordinate) => void;
  updateEndPoint: (coord: Coordinate) => void;
  updatePath: (path: Coordinate[]) => Promise<void>;
  updatePoints: (
    points: Coordinate,
    name: string,
    options?: { addCheckpoint?: boolean; centroid?: boolean },
  ) => Promise<void>;
  updateBoundary: (boundary: Coordinate[][], zone_id: number) => Promise<void>;
  type: MapType;
  selectInteractionDisabled: boolean;
  modifyInteractionDisabled: boolean;
  selectableFeatures: KmlFeatureType[];
}

interface MapInteractionsLayerState {
  // This layer doesn't own any renderable features — after the
  // KmlLayer split into CorePointsLayer / CentroidsLayer / PathLayer /
  // DumpEventsLayer / StaticPointsLayer / ZonesLayer it only hosts the
  // Select / Modify interactions and the click-dispatch logic. State
  // is empty.
}

/**
 * MapInteractionsLayer — hosts the OpenLayers `Select` + `Modify`
 * interactions for the map and routes user clicks through the
 * priority-based dispatch pipeline to the sidebar action candidates
 * (delete / change zone type / set waypoint / skip / move point).
 *
 * This is the vestigial "controller" left over from the old monolithic
 * KmlLayer after all its rendering responsibilities moved into focused
 * sibling layers (CorePointsLayer / CentroidsLayer / PathLayer /
 * DumpEventsLayer / StaticPointsLayer / ZonesLayer). The Select
 * interaction hit-tests across every vector layer on the map, so the
 * click-dispatch pipeline still fires for features rendered by any
 * of those siblings.
 *
 * Responsibilities:
 *   - Host the `<SelectReact>` / `<ModifyReact>` interactions
 *   - Resolve overlapping hits via `SELECTION_PRIORITIES`
 *   - Route the selected feature to the correct sidebar candidate
 *     callback (addDeleteCandidate / addSkipCandidate / etc)
 *   - Re-fire those candidates on mission updates so the sidebar
 *     stays in sync with the live geometry
 *   - Handle "clicked on empty space" to deselect
 *
 * Does NOT handle:
 *   - Rendering any features (sibling layers)
 *   - Storing the selected-features Collection (shared SelectionManager)
 *   - Draggable filter rules (helpers/selectionRules.ts)
 *   - POLY_SELECTED sidebar-row-click (ZonesLayer)
 */

export default class MapInteractionsLayer extends PureComponent<MapInteractionsLayerProps, MapInteractionsLayerState> {
  static contextType = MapContext;
  declare context: React.ContextType<typeof MapContext>;

  // Feature-type → action maps, SELECTION_PRIORITIES, and
  // NON_DRAGGABLE_FEATURES_BY_MAP / BY_JOB moved to
  // ../helpers/selectionRules.ts in Step 10 — they're pure data, not
  // behaviour, and ZonesLayer needs the same filter logic for its
  // POLY_SELECTED handler. This layer only imports what it uses.

  selectable: boolean;
  // TODO not 100% sure about this typing...
  pendingSelectFeatures: Feature<SimpleGeometry>[];
  deselectTimer: NodeJS.Timeout | undefined;
  handleNothingTimeout: NodeJS.Timeout;
  nextHandleId: NodeJS.Timeout;
  waitForMapTimer: NodeJS.Timeout | undefined;

  mapClickListenerKey: any = null;

  /**
   * Raw OL interactions, created once in componentDidMount and
   * attached directly to the map. We can't use react-openlayers'
   * `SelectReact` here because it wraps `Select` with a `layers`
   * filter that only hits the vector layer it's nested inside via
   * `<source.VectorSourceReact>`. Since this layer's own source is
   * empty, that filter rejects every sibling-layer feature — zones
   * become unclickable. Constructing Select directly with no
   * `layers` option defaults OL to hit-testing every vector layer
   * on the map, which is what we want.
   */
  private selectInteraction: Select | null = null;
  private modifyInteraction: Modify | null = null;

  private lastClickedCoordinate: Coordinate | null = null;

  /**
   * Select interaction filter — reference overlays (the MapMaker
   * scheduling layer) never take a selection, so the click passes
   * through to the mission geometry below them.
   */
  private filterSelectableFeature = (feature: FeatureLike): boolean => !isScheduleOverlay(feature.getProperties());

  constructor(props: MapInteractionsLayerProps) {
    super(props);

    this.state = {};

    this.handleSelectFeature = this.handleSelectFeature.bind(this);
    this.removeFeatureSelection = this.removeFeatureSelection.bind(this);
    this.handleNothingClicked = this.handleNothingClicked.bind(this);
    this.onSelectionChanged = this.onSelectionChanged.bind(this);
    this.refreshSelectionCandidates = this.refreshSelectionCandidates.bind(this);

    this.selectable = true;
    this.pendingSelectFeatures = [];
  }

  attachMapClickListener = () => {
    // Add click listener to the map for handling clicks on empty space
    const map = this.context?.map;

    if (!map) {
      return;
    }

    // Remove existing listener first (if any)
    this.removeMapClickListener();

    // Add the click listener
    this.mapClickListenerKey = map.on('singleclick', (evt: any) => {
      // Don't deselect features while a drawing interaction is active (e.g. eraser, cutter)
      // otherwise clicking on the map to draw points clears the selected feature
      if (this.props.selectInteractionDisabled) {
        return;
      }

      // Must use the SAME hitTolerance as the Select interaction
      // (`attachInteractions` → isSamplingMap ? 40 : 20). Without this,
      // a click within Select's tolerance but outside a 0-pixel hit box
      // triggers Select (selects the feature) AND this listener
      // (`handleNothingClicked` → 300 ms deselect timer), producing a
      // flash-select-then-deselect regression.
      const hitTolerance = this.props.type === MAP_TYPES.SAMPLING ? 40 : 20;
      const featuresAtPixel = map.getFeaturesAtPixel(evt.pixel, { hitTolerance });
      // Reference overlays (the MapMaker scheduling layer) are filtered
      // out by the Select interaction, so a click that lands only on one
      // must count as empty space here too — otherwise Select clears its
      // internal collection while the shared store still believes the
      // previous feature is selected.
      const missionFeatures = featuresAtPixel?.filter((feature) => !isScheduleOverlay(feature.getProperties()));

      if (!missionFeatures || missionFeatures.length === 0) {
        // No mission features clicked, handle as nothing clicked
        this.handleNothingClicked();
      }
    });
  };

  removeMapClickListener = () => {
    const map = this.context?.map;

    if (!map) {
      return;
    }

    if (!this.mapClickListenerKey) {
      return;
    }

    // Remove the click listener
    map.un('singleclick', this.mapClickListenerKey);
  };

  waitForMap = () => {
    if (this.context?.map) {
      // Map is ready
      this.attachMapClickListener();
      this.attachInteractions();
    } else {
      // Track the timer so `componentWillUnmount` can cancel it. Any
      // unmount while this setTimeout is pending (mission swap in
      // BoundaryApproval, StrictMode dev-mode double-invoke, CommonMap
      // re-render with new `mission` gate) otherwise leaves the stale
      // callback in the event loop. When it fires, it runs on the OLD
      // (unmounted) instance, which still holds a live `context.map`
      // ref and `this.selectInteraction === null` (cleared in
      // `detachInteractions`), so `attachInteractions` creates a SECOND
      // Select interaction and attaches it to the same map the new
      // instance already attached to. Every user click then fires
      // 'select' on both interactions — first fire selects the
      // feature, second fire sees `selectionManager` already populated
      // with the same ref and trips the same-ref double-click-
      // deselect guard → feature is deselected milliseconds after
      // being selected.
      this.waitForMapTimer = setTimeout(this.waitForMap, 100);
    }
  };

  /**
   * Create the Select and Modify interactions and attach them to the
   * map. Runs once the map context is available (might be a short
   * wait after mount — see `waitForMap`).
   *
   * Both interactions are constructed WITHOUT a `layers` filter so OL
   * hit-tests every vector layer in the map. The old SelectReact /
   * ModifyReact wrappers implicitly filtered to the parent
   * VectorSourceReact's layer, which stopped working once the
   * features moved into sibling layers with their own sources.
   */
  private attachInteractions() {
    const map = this.context?.map;

    if (!map || this.selectInteraction) {
      return;
    }

    const isSamplingMap = this.props.type === MAP_TYPES.SAMPLING;

    this.selectInteraction = new Select({
      style: null,
      hitTolerance: isSamplingMap ? 40 : 20,
      // Reference overlays (the MapMaker scheduling layer's field
      // outlines, pullins and order lines) are not mission geometry.
      // Rejecting them lets the hit test fall through to the mission
      // feature underneath — Select is single-feature (`multi: false`),
      // so without this filter a scheduled field's outline would steal
      // the click from the mission's own field zone and be treated as
      // an editable FIELD of the mission being mapped.
      filter: this.filterSelectableFeature,
      // Do NOT share OL Select's features collection with
      // `selectionManager.selectedFeatures`. The two MUST stay
      // separate, because `handleSelectFeatures` reads the current
      // selection from `selectionManager` to drive the "click twice
      // to delete" logic — if OL Select's click already pushed the
      // feature into the shared collection, that logic fires on
      // every single click and triggers an unwanted deselect.
      // We sync OL Select's internal collection manually in
      // `updateSelectFeatures` instead.
    });
    this.selectInteraction.setActive(!this.props.selectInteractionDisabled);
    this.selectInteraction.on('select', this.handleSelectFeature as any);
    // OL type friction: the @types/ol version pulled transitively here
    // declares `Interaction` with a slightly different private-field
    // set than what `new Select()` produces. `as any` sidesteps the
    // structural type check without runtime cost.
    map.addInteraction(this.selectInteraction as any);

    this.modifyInteraction = new Modify({
      features: selectionManager.draggableFeatures,
      pixelTolerance: isSamplingMap ? 20 : 10,
      deleteCondition: doubleClick,
      // Intentionally NOT setting `insertVertexCondition` — OL Modify
      // defaults to `always`, which allows the user to click any
      // point on an edge to insert a new vertex. Passing `() => false`
      // here would disable that, and passing `false` (truthy check
      // fails in OL's constructor → falls back to default) would be
      // equivalent to omitting it. Omit explicitly to stay clear.
    });
    this.modifyInteraction.setActive(!this.props.modifyInteractionDisabled);
    this.modifyInteraction.on('modifyend', this.handleKmlModify as any);
    map.addInteraction(this.modifyInteraction as any);
  }

  private detachInteractions() {
    const map = this.context?.map;

    if (this.selectInteraction) {
      this.selectInteraction.un('select', this.handleSelectFeature as any);
      map?.removeInteraction(this.selectInteraction as any);
      this.selectInteraction = null;
    }

    if (this.modifyInteraction) {
      this.modifyInteraction.un('modifyend', this.handleKmlModify as any);
      map?.removeInteraction(this.modifyInteraction as any);
      this.modifyInteraction = null;
    }
  }

  componentDidMount() {
    // For benchmarking performance
    const benchmarkFilename = queryParams.get('benchmark-filename');

    if (benchmarkFilename) {
      performance.mark('start');
    }

    // All feature rendering lives in sibling layers now
    // (CorePointsLayer, CentroidsLayer, PathLayer, DumpEventsLayer,
    // StaticPointsLayer, ZonesLayer). MapInteractionsLayer only owns
    // the Select / Modify interactions and re-fires the sidebar
    // action candidates when the selection or underlying mission
    // changes. POLY_SELECTED (sidebar "click a zone row" event) is
    // handled directly by ZonesLayer since only zone features have
    // poly_id.
    //
    // Fired by SelectionManager on every mutation (set/clear) of the
    // shared selectedFeatures Collection. Triggers a refresh of the
    // sidebar action candidates.
    EventBus.on(MAP_EVENTS.SELECTION_CHANGED, this.onSelectionChanged);
    // When the underlying mission changes, the currently-selected
    // feature's geometry / action-eligibility may have changed too —
    // refresh the candidates so the sidebar buttons stay in sync.
    // We listen to both the generic UPDATED event (catch-all) AND
    // the narrower ZONES_UPDATED / STATIC_POINTS_UPDATED events that
    // some callers fire instead — the latter skip the generic event
    // to avoid triggering core rebuilds on non-core mutations.
    EventBus.on(MISSION_EVENTS.UPDATED, this.refreshSelectionCandidates);
    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, this.refreshSelectionCandidates);
    EventBus.on(MAP_EVENTS.CORES_NEED_UPDATE, this.refreshSelectionCandidates);
    EventBus.on(MAP_EVENTS.ZONES_UPDATED, this.refreshSelectionCandidates);
    EventBus.on(MAP_EVENTS.STATIC_POINTS_UPDATED, this.refreshSelectionCandidates);

    // Try to attach interactions immediately if the map context is
    // already available (unit tests inject it synchronously). If not,
    // `waitForMap` retries asynchronously.
    if (this.context?.map) {
      this.attachInteractions();
    }

    this.waitForMap();
  }

  componentDidUpdate(prevProps: MapInteractionsLayerProps) {
    if (prevProps.selectInteractionDisabled !== this.props.selectInteractionDisabled) {
      this.selectInteraction?.setActive(!this.props.selectInteractionDisabled);
    }

    if (prevProps.modifyInteractionDisabled !== this.props.modifyInteractionDisabled) {
      this.modifyInteraction?.setActive(!this.props.modifyInteractionDisabled);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.waitForMapTimer);
    this.waitForMapTimer = undefined;
    this.removeMapClickListener();
    this.detachInteractions();

    EventBus.remove(MAP_EVENTS.SELECTION_CHANGED, this.onSelectionChanged);
    EventBus.remove(MISSION_EVENTS.UPDATED, this.refreshSelectionCandidates);
    EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, this.refreshSelectionCandidates);
    EventBus.remove(MAP_EVENTS.CORES_NEED_UPDATE, this.refreshSelectionCandidates);
    EventBus.remove(MAP_EVENTS.ZONES_UPDATED, this.refreshSelectionCandidates);
    EventBus.remove(MAP_EVENTS.STATIC_POINTS_UPDATED, this.refreshSelectionCandidates);
  }

  /**
   * Re-fire the sidebar action candidates (delete / skip / etc) for
   * the currently-selected feature. Called whenever the mission data
   * changes — the selected feature's geometry or action eligibility
   * may have shifted (e.g. the user dragged a zone corner, which
   * updates the OL Feature's geometry in place).
   *
   * Reads the selected feature directly from the shared
   * SelectionManager — no `mission.to_feature` iteration needed, and
   * no kmlFeatures state. Historical note: this replaces the old
   * `initKml` / `checkSelectNeedsUpdate` pair which walked
   * `mission.to_feature(prevFeatures)` on every tick just to find the
   * one feature that was already in our hand.
   */
  refreshSelectionCandidates() {
    const selected = selectionManager.selectedFeatures.getArray()[0];

    if (!selected) {
      return;
    }

    const selectedType = getFeatureType(selected.getProperties());

    if (!selectedType) {
      return;
    }

    const actions = [
      [DELETEABLE_FEATURES_BY_JOB[this.props.jobType], this.props.addDeleteCandidate] as const,
      [ZONE_TYPE_CHANGEABLE_FEATURES_BY_JOB[this.props.jobType], this.props.addZoneTypeChangeCandidate] as const,
      [WAYPOINT_SETTABLE_FEATURES_BY_JOB[this.props.jobType], this.props.addWaypointCandidate] as const,
      [SKIP_SETTABLE_FEATURES_BY_JOB[this.props.jobType], this.props.addSkipCandidate] as const,
      [POINT_MOVABLE_FEATURES_BY_JOB[this.props.jobType], this.props.addMoveCandidate] as const,
    ];

    const geometry = selected.getGeometry();
    const coords = geometry ? geometry.getCoordinates() : [];

    for (const [ACTIONABLE_FEATURES, addCandidate] of actions) {
      if (!ACTIONABLE_FEATURES.includes(selectedType)) {
        continue;
      }

      addCandidate({
        feature: selected,
        type: selectedType,
        removeFunc: this.removeFeatureSelection,
        coordinates: findMaxLat(coords as number[][]) ?? (coords as Coordinate),
      });
    }
  }

  removeFeatureSelection() {
    this.updateSelectFeatures([]);
  }

  /**
   * Fires when the shared SelectionManager's selectedFeatures Collection
   * has been mutated (by this layer or by another layer). Re-fires the
   * sidebar action candidates for the now-selected feature.
   */
  onSelectionChanged() {
    this.refreshSelectionCandidates();
  }

  updateSelectFeatures(selectedFeatures: Feature<SimpleGeometry>[]) {
    // TODO should pass down actual job type here instead of inferring from props boolean
    const missionType: JobType = this.props.zoneMission ? 'Zone' : 'Grid';
    const draggable = filterDraggable(selectedFeatures, this.props.type, missionType);

    // setSelected fires SELECTION_CHANGED synchronously, which triggers
    // onSelectionChanged -> refreshSelectionCandidates on this layer
    // and rebuilds on every sibling layer that listens. The
    // setDraggable call reuses the same stable Collection reference
    // that Modify is bound to — mutating in place preserves the
    // interaction.
    selectionManager.setSelected(selectedFeatures);
    selectionManager.setDraggable(draggable);

    // Keep OL Select's internal features collection in sync with our
    // shared store. Without this, clicking empty then re-clicking the
    // same feature is a silent no-op: OL Select sees the feature as
    // "already selected" and skips firing the 'select' event
    // (default `toggleCondition = never` means clicks on already-
    // selected features are ignored). OL Select already clears its
    // internal features on an empty click via its own handleEvent,
    // but on the programmatic `deselectAll` / `removeFeatureSelection`
    // paths we need to clear it ourselves.
    if (this.selectInteraction) {
      const olFeatures = this.selectInteraction.getFeatures();
      olFeatures.clear();

      for (const f of selectedFeatures) {
        olFeatures.push(f);
      }
    }
  }

  isFeatureSimpleGeometry(feature: FeatureLike): feature is Feature<SimpleGeometry> {
    const geometry = feature.getGeometry();

    return !!geometry && 'getCoordinates' in geometry;
  }
  /**
   * Handle for selecting a feature, determines if a feature can be deleted
   * @param {*} event
   */
  handleSelectFeature(event: BaseEvent | SelectEvent) {
    // Type guard to check if this is a SelectEvent
    if (!('selected' in event)) {
      return;
    }

    // Store the clicked coordinate from the map browser event
    if (event.mapBrowserEvent && event.mapBrowserEvent.coordinate) {
      this.lastClickedCoordinate = event.mapBrowserEvent.coordinate;
    }

    const feature = event.selected[0];

    if (!feature) {
      return;
    }

    clearTimeout(this.handleNothingTimeout);

    if (!this.isFeatureSimpleGeometry(feature)) {
      return;
    }

    //const featureType = getFeatureType(feature.getProperties());
    //const featureKey = getKey(feature.getProperties(), featureType);
    //console.log(`KmlLayer handleSelectFeature type=${featureType}, key=${featureKey}`);//: ${JSON.stringify(feature)}`);

    this.pendingSelectFeatures.push(feature);
    clearTimeout(this.nextHandleId);
    this.nextHandleId = setTimeout(() => this.handleSelectFeatures(), 0);
  }

  deselectAll = () => {
    this.updateSelectFeatures([]);
    this.props.addDeleteCandidate(null);
    this.props.addZoneTypeChangeCandidate(null);
    this.props.addWaypointCandidate(null);
    this.props.addSkipCandidate(null);
    this.props.addMoveCandidate(null);
    this.pendingSelectFeatures = [];
  };

  handleSelectFeatures() {
    this.pendingSelectFeatures.sort(
      (feature1, feature2) =>
        SELECTION_PRIORITIES[getFeatureType(feature2.getProperties())!] -
        SELECTION_PRIORITIES[getFeatureType(feature1.getProperties())!],
    );
    //console.log(`pendingSelectFeatures length=${this.pendingSelectFeatures.length}`)
    const priorityFeature = this.pendingSelectFeatures[0];

    if (!priorityFeature) {
      this.deselectAll();

      return;
    }

    const priorityFeatureType = getFeatureType(priorityFeature.getProperties());

    if (!priorityFeatureType) {
      this.deselectAll();

      return;
    }

    if (!this.props.selectableFeatures.includes(priorityFeatureType)) {
      this.deselectAll();

      return;
    }

    const currentlySelectedFeatures = selectionManager.selectedFeatures.getArray();
    const hasCurrentlySelectedFeatures = currentlySelectedFeatures.length > 0;

    // Double-click-to-deselect guard. Must compare by OL Feature object
    // identity — NOT by `getKey` equality. `getKey` returns `undefined`
    // for the `default:` switch case (FIELD, PULLIN, PARKING_POINT,
    // STARTING_POINT, END_POINT — all selectable types without a case),
    // so key-based comparison fires `undefined === undefined === true`
    // and falsely deselects whenever:
    //   - a stale Feature ref from a previous mission is still sitting
    //     in selectionManager (e.g. BoundaryApproval job swap) and the
    //     user clicks the new mission's FIELD, or
    //   - a rebuild has replaced the Feature object for the same
    //     logical boundary (different refs, same undefined key).
    // Identity compare: same ref → intended double-click-deselect;
    // different ref → select the newly clicked one.
    if (hasCurrentlySelectedFeatures && currentlySelectedFeatures[0] === priorityFeature) {
      this.deselectTimer = setTimeout(() => {
        this.deselectAll();
      }, 300);
      this.pendingSelectFeatures = [];

      return;
    }

    this.updateSelectFeatures([priorityFeature]);

    for (const [ACTIONABLE_FEATURES, addCandidate] of [
      [DELETEABLE_FEATURES_BY_JOB, this.props.addDeleteCandidate] as const,
      [ZONE_TYPE_CHANGEABLE_FEATURES_BY_JOB, this.props.addZoneTypeChangeCandidate] as const,
      [SKIP_SETTABLE_FEATURES_BY_JOB, this.props.addSkipCandidate] as const,
      [WAYPOINT_SETTABLE_FEATURES_BY_JOB, this.props.addWaypointCandidate] as const,
      [POINT_MOVABLE_FEATURES_BY_JOB, this.props.addMoveCandidate] as const,
    ]) {
      if (ACTIONABLE_FEATURES[this.props.jobType].includes(priorityFeatureType)) {
        const geometry = priorityFeature.getGeometry()!;

        addCandidate({
          feature: priorityFeature,
          type: priorityFeatureType,
          removeFunc: this.removeFeatureSelection,
          coordinates: this.lastClickedCoordinate
            ? geometry.getClosestPoint(this.lastClickedCoordinate)
            : findMaxLat(geometry.getCoordinates())!,
        });
      } else {
        addCandidate(null);
      }
    }

    this.pendingSelectFeatures = [];
  }

  /**
   * Handle feature modification based on type
   * @param {*} event
   */
  // ((event: T | Event) => void) | void
  handleKmlModify = async (event: ModifyEvent) => {
    clearTimeout(this.deselectTimer);
    this.deselectTimer = undefined;
    const features = event.features.getArray();

    if (features.length) {
      const properties = features[0].getProperties();
      const type = getFeatureType(properties);
      const coordinates = properties.geometry.getCoordinates();

      switch (type) {
        case KML_FEATURE_TYPE.PULLIN:
          this.props.updatePullin(coordinates);
          break;
        case KML_FEATURE_TYPE.PARKING_POINT:
          this.props.updateParkingPoint(coordinates);
          break;
        case KML_FEATURE_TYPE.STARTING_POINT:
          this.props.updateStartingPoint(coordinates);
          break;
        case KML_FEATURE_TYPE.END_POINT:
          this.props.updateEndPoint(coordinates);
          break;
        case KML_FEATURE_TYPE.CENTROID:
          await this.props.updatePoints(coordinates, properties.name, { centroid: true });
          break;
        case KML_FEATURE_TYPE.CORE_POINT:
          await this.props.updatePoints(coordinates, properties.name);
          break;
        case KML_FEATURE_TYPE.PATH:
          await this.props.updatePath(coordinates);
          break;
        case KML_FEATURE_TYPE.FIELD:
        case KML_FEATURE_TYPE.GENERIC_ZONE:
        case KML_FEATURE_TYPE.COLLECTED_FIELD:
        case KML_FEATURE_TYPE.UNSAFE:
        case KML_FEATURE_TYPE.SLOW:
        case KML_FEATURE_TYPE.SAMPLE_ZONE:
          await this.props.updateBoundary(coordinates, properties.zone_id);
          break;
        default:
      }
    }
  };

  handleNothingClicked() {
    this.handleNothingTimeout = setTimeout(() => {
      this.props.addDeleteCandidate(null);
      this.props.addZoneTypeChangeCandidate(null);
      this.props.addWaypointCandidate(null);
      this.props.addSkipCandidate(null);
      this.props.addMoveCandidate(null);
      this.updateSelectFeatures([]);
    }, 300); // at one point, this was changed to 100ms and the "double click to delete" feature stopped working
    // this is likely timed with the deselectTimer and is really a race condition waiting to happen, but for now
    // we can change this and just move on... these two constants should probably at least be the same value
    // but given the current weird beavior, we will leave it as is for now
  }

  render() {
    // This component has no visual output. It exists purely to host
    // the Select and Modify OL interactions (attached directly to the
    // map in componentDidMount) and to route their events through the
    // click-dispatch pipeline to the sidebar action candidates.
    // `componentDidUpdate` reacts to `selectInteractionDisabled` /
    // `modifyInteractionDisabled` prop changes.
    return null;
  }
}
